import React, { useState, useEffect } from 'react';
import { X, Gift, Loader2, Sparkles, Home, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API_PATHS } from '../constants/api-constants';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { MAIN_CATEGORIES, SUB_CATEGORIES } from '../constants/categories';

interface CategorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCategory: string;
  onCategorySelect: (category: string) => void;
}

export function CategorySidebar({ isOpen, onClose, selectedCategory, onCategorySelect }: CategorySidebarProps) {
  const [counts, setCounts] = useState<{ all: number | null; gold: number | null; silver: number | null }>({
    all: null,
    gold: null,
    silver: null,
  });
  const [showOffersDialog, setShowOffersDialog] = useState(false);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const response = await fetch(API_PATHS.FETCH_PRODUCT_COUNTS);
        if (response.ok) {
          const data = await response.json();
          setCounts(data);
        }
      } catch (error) {
        console.error('Error fetching product counts:', error);
      }
    };
    fetchCounts();
  }, []);

  const categories = MAIN_CATEGORIES.map(cat => ({
    ...cat,
    count: cat.id === 'all' ? counts.all : cat.id === 'gold' ? counts.gold : counts.silver
  }));

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-80 bg-white border-r border-border z-50 transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-30
      `}>
        {/* Mobile Header */}
        <div className="bg-brand-green p-4 lg:hidden flex items-center justify-between">
          <Link 
            to="/" 
            onClick={onClose}
            className="flex items-center space-x-2 text-white hover:text-brand-gold transition-colors"
          >
            <Home className="h-5 w-5" />
            <span className="font-serif tracking-wide text-lg">Home</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-white hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <ScrollArea className="h-[calc(100%-64px)] lg:h-full">
          <div className="p-4 space-y-2">
            {categories.map((category) => {
              const Icon = category.icon;
              const isSelected = selectedCategory === category.id || 
                                (SUB_CATEGORIES[category.id] && SUB_CATEGORIES[category.id].some(s => s.id === selectedCategory));
              const showSubcategories = isSelected && SUB_CATEGORIES[category.id];

              return (
                <div key={category.id}>
                  <button
                    onClick={() => onCategorySelect(category.id)}
                    className={`
                      w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors
                      ${isSelected
                        ? 'bg-brand-green text-white'
                        : 'hover:bg-brand-green/10 text-foreground'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="h-5 w-5" />
                      <span>{category.name}</span>
                    </div>
                    <span className={`
                      text-xs px-2 py-1 rounded-full flex items-center justify-center min-w-[2rem]
                      ${isSelected ? 'bg-white/20' : 'bg-muted text-muted-foreground'}
                    `}>
                      {category.count ?? 0}
                    </span>
                  </button>

                  {showSubcategories && (
                    <div className="ml-8 mt-2 space-y-1 animate-in fade-in slide-in-from-top-2 duration-300">
                      {SUB_CATEGORIES[category.id].map((sub: any) => (
                        <button
                          key={sub.id}
                          onClick={() => onCategorySelect(sub.id)}
                          className={`
                            w-full flex items-center justify-between p-2 rounded text-left text-sm transition-colors
                            ${selectedCategory === sub.id
                              ? 'bg-brand-green/20 text-brand-green'
                              : 'hover:bg-brand-green/5 text-muted-foreground'
                            }
                          `}
                        >
                          <span>{sub.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Separator className="mx-4 my-4" />

          {/* Additional Links */}
          <div className="px-4 py-2">
            <Link 
              to="/about"
              onClick={onClose}
              className="w-full flex items-center space-x-3 p-3 rounded-lg text-foreground hover:bg-brand-green/10 transition-colors"
            >
              <Info className="h-5 w-5 text-brand-green" />
              <span>About Us</span>
            </Link>
          </div>

          <div className="p-4">
            <div className="bg-gradient-to-br from-brand-green/10 to-brand-gold/10 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Gift className="h-5 w-5 text-brand-green" />
                <span className="text-sm font-medium">Special Offers</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                Get exclusive deals on premium jewellery collections
              </p>
              <Button 
                size="sm" 
                className="w-full bg-brand-green hover:bg-brand-green/90 text-white"
                onClick={() => setShowOffersDialog(true)}
              >
                View Offers
              </Button>
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* No Offers Dialog */}
      <Dialog open={showOffersDialog} onOpenChange={setShowOffersDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center items-center">
            <div className="mx-auto bg-gradient-to-br from-brand-green/10 to-brand-gold/10 p-4 rounded-full mb-3">
              <Sparkles className="h-8 w-8 text-brand-green" />
            </div>
            <DialogTitle className="text-xl text-brand-green">No Offers Available</DialogTitle>
            <DialogDescription className="text-center text-muted-foreground pt-2">
              We don't have any offers currently. Stay tuned — exciting deals on our premium jewellery collections are coming soon!
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-2">
            <Button
              className="bg-brand-green hover:bg-brand-green/90"
              onClick={() => setShowOffersDialog(false)}
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}